import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from "react"
import { computeEffectiveMaxHeight, fitFontSize, getDefaultTextBoxPadding } from "./text-box-layout"
import { applyUvInsertTextEdit } from "@shared/view/uv-inserts/text-input-rules"

const DEFAULT_FONT_PX = 21

export type VisualiserTextBoxViewModelOptions = {
  font: string
  fontWeight: string
  /** Bare numbers become `Npx` and support auto-shrink; strings are applied as CSS `font-size` verbatim. */
  fontSize?: number | string
  /** Auto-shrink floor in px; `fitFontSize`'s 8px when unset. */
  minFontSize?: number
  lineHeight: number
  size?: {
    width: string
    minHeight: string
    maxHeight: string
    minWidth: string
    maxWidth: string
  }
  position?: {
    top?: string
    bottom?: string
    left?: string
    right?: string
  }
  maxLength?: number
  maxLines?: number
  /** Print wrap width; with `maxLines` it caps the wrapped line count, not just hard line breaks. */
  maxCharsPerLine?: number
  initialValue: string
  userEditable: boolean
  value?: string
  color?: string
  style?: CSSProperties
  /** Overrides the browser-default placeholder colour; any opacity comes from the colour's alpha. */
  placeholderColor?: string
  setValue: (value: string) => void
}

function readVerticalPadding(el: HTMLElement): number {
  const cs = window.getComputedStyle(el)
  return (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
}

function layoutTextElement(
  el: HTMLTextAreaElement | HTMLDivElement,
  options: {
    adjustedFontSize: number
    fontSize: number | string
    minFontSize?: number
    lineHeight: number
    maxLines?: number
    maxHeight?: string
    setAdjustedFontSize: (n: number) => void
  },
) {
  const { adjustedFontSize, fontSize, minFontSize, lineHeight, maxLines, maxHeight, setAdjustedFontSize } = options

  if (typeof fontSize === "string") {
    el.style.fontSize = fontSize
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
    return
  }

  // `scrollHeight` includes padding, so the maxLines cap must too or padding eats into the text.
  // Padding may be em-based: read it at the configured size, which is what the cap describes.
  el.style.fontSize = `${fontSize}px`
  const effectiveMaxHeight = computeEffectiveMaxHeight({
    maxHeight,
    maxLines,
    lineHeight,
    fontSize,
    verticalPadding: readVerticalPadding(el),
  })

  const optimalFontSize = fitFontSize({
    fontSize,
    effectiveMaxHeight,
    minFontSize,
    measure: (fs) => {
      el.style.fontSize = `${fs}px`
      el.style.height = "auto"
      return el.scrollHeight
    },
  })

  if (optimalFontSize !== adjustedFontSize) {
    setAdjustedFontSize(optimalFontSize)
  }
  el.style.fontSize = `${optimalFontSize}px`
  el.style.height = "auto"
  el.style.height = `${el.scrollHeight}px`
}

/**
 * Text measurement, sanitisation, and layout for the visualiser text box (editable textarea or static label).
 */
export function useVisualiserTextBoxViewModel(options: VisualiserTextBoxViewModelOptions) {
  const {
    font,
    fontWeight,
    fontSize: fontSizeProp,
    minFontSize,
    lineHeight,
    size,
    position,
    color,
    maxLength,
    maxLines,
    maxCharsPerLine,
    initialValue,
    userEditable,
    value,
    style: extraStyle,
    placeholderColor,
    setValue,
  } = options

  const baseFontSize = fontSizeProp ?? DEFAULT_FONT_PX

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const staticRef = useRef<HTMLDivElement>(null)
  const [adjustedFontSize, setAdjustedFontSize] = useState(() =>
    typeof baseFontSize === "number" ? baseFontSize : DEFAULT_FONT_PX,
  )

  useEffect(() => {
    if (typeof baseFontSize === "number") {
      setAdjustedFontSize(baseFontSize)
    }
  }, [baseFontSize])

  const adjustHeight = useCallback(() => {
    const el = userEditable ? textareaRef.current : staticRef.current
    if (!el) {
      return
    }
    layoutTextElement(el, {
      adjustedFontSize,
      fontSize: baseFontSize,
      minFontSize,
      lineHeight,
      maxLines,
      maxHeight: size?.maxHeight,
      setAdjustedFontSize,
    })
  }, [
    adjustedFontSize,
    baseFontSize,
    minFontSize,
    lineHeight,
    maxLines,
    size?.maxHeight,
    userEditable,
  ])

  useEffect(() => {
    adjustHeight()
  }, [adjustHeight, value, baseFontSize, size?.maxHeight, maxLines, lineHeight, userEditable])

  useEffect(() => {
    if (!userEditable) {
      return
    }
    if (value === undefined) {
      setValue(initialValue)
    }
  }, [])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const target = e.target
      const raw = target.value
      const previous = value ?? ""
      const next = applyUvInsertTextEdit({
        previous,
        raw,
        maxLength: maxLength ?? Number.POSITIVE_INFINITY,
        maxLines,
        maxCharsPerLine,
      })
      if (next === previous && raw !== previous) {
        // A rejected mid-message edit: React puts the old value back and the caret jumps to the end,
        // so put it back where the customer was typing.
        const caret = Math.max(0, Math.min(previous.length, target.selectionStart - (raw.length - previous.length)))
        requestAnimationFrame(() => target.setSelectionRange(caret, caret))
      }
      setValue(next)
    },
    [value, maxLength, maxLines, maxCharsPerLine, setValue],
  )

  const displayValue = value ?? ""

  const fontSizeCss: string =
    typeof baseFontSize === "number" ? `${adjustedFontSize}px` : baseFontSize


  const textareaStyle: CSSProperties = {
    position: "absolute",
    top: position?.top,
    bottom: position?.bottom,
    left: position?.left,
    right: position?.right,
    width: size?.width || "100%",
    minWidth: size?.minWidth,
    maxWidth: size?.maxWidth,
    minHeight: size?.minHeight,
    maxHeight: size?.maxHeight,
    fontFamily: font,
    fontSize: fontSizeCss,
    fontWeight: fontWeight,
    // A custom placeholder colour carries its own alpha, so the empty-state dim would double up.
    opacity: userEditable ? (!value && !placeholderColor ? 0.5 : 1) : 1,
    lineHeight: lineHeight,
    color: color || "var(--color-black)",
    // Script fonts get ink room inside the clip box; config `style` below may still override it.
    padding: getDefaultTextBoxPadding(font),
    ...(userEditable ? {} : { whiteSpace: "pre-wrap" as const }),
    ...(placeholderColor
      ? ({ "--visualiser-placeholder-color": placeholderColor } as CSSProperties)
      : {}),
    ...extraStyle,
  }

  return {
    textareaRef,
    staticRef,
    handleChange,
    textareaStyle,
    userEditable,
    placeholder: initialValue,
    displayValue,
  }
}
